import * as React from 'react';
import Tag from 'antd/lib/tag';
import Avatar from 'antd/lib/avatar';
import Collapse from 'antd/lib/collapse';
import Rate from 'antd/lib/rate';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import dayjs from 'dayjs';
import { ExportOutlined } from '@ant-design/icons';
import { AppStoreType, IntegrationType, IssueSource, IssueSourceType } from '@/types';
import { antGrey, getAvatarColorForName, rwColors } from '@/components/styles/globalStyles';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { getIntegrationTypeForAppStoreType, tagColorForStatusCategory } from '@/helpers';
import runwayLogo from '../../../assets/integration-runway.svg';

const ROW_STYLE: React.CSSProperties = {
    margin: '0 16px',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
};

const ROW_BORDER_STYLE: React.CSSProperties = { ...ROW_STYLE, borderTop: `1px solid ${antGrey.grey4}` };

const personRow = (label: string, name?: string) => {
    if (!name) {
        return null;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                    size={20}
                    style={{ backgroundColor: getAvatarColorForName(name), fontSize: 11, border: 'none' }}
                >
                    {name.charAt(0).toUpperCase()}
                </Avatar>
                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{name}</Text>
            </div>
        </div>
    );
};

const PMTicketSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const ticket = source.pmTicket;
    if (!ticket) {
        return null;
    }
    return (
        <>
            <div style={ROW_STYLE}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                        {ticket.projectName &&
                            (ticket.projectUrl ?
                                <a href={ticket.projectUrl} target="_blank" rel="noreferrer">
                                    <Text style={{ color: rwColors.primary, fontSize: 14 }}>{ticket.projectName}</Text>
                                </a>
                            :   <Text style={{ color: antGrey.grey7, fontSize: 14 }}>{ticket.projectName}</Text>)}
                    </div>
                    {ticket.ticketUrl && (
                        <a
                            href={ticket.ticketUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                        >
                            <Text style={{ color: rwColors.primary, fontSize: 14 }}>{ticket.ticketId}</Text>
                        </a>
                    )}
                </div>
                <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{ticket.ticketTitle}</Text>
                {ticket.labels?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {ticket.labels.map((label) => (
                            <Tag
                                key={label}
                                style={{
                                    margin: 0,
                                    background: antGrey.grey4,
                                    border: `1px solid ${antGrey.grey4}`,
                                    color: antGrey.grey8,
                                }}
                            >
                                {label}
                            </Tag>
                        ))}
                    </div>
                )}
            </div>
            <div style={ROW_BORDER_STYLE}>
                {ticket.ticketDescription && (
                    <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                        {ticket.ticketDescription}
                    </Paragraph>
                )}
                {personRow('Assignee', ticket.assignee)}
            </div>
            {ticket.reporter && <div style={ROW_BORDER_STYLE}>{personRow('Reporter', ticket.reporter)}</div>}
        </>
    );
};

const UserReviewsSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.userReviews;
    if (!data) {
        return null;
    }
    const reviews = data.sampleReviews ?? [];
    if (reviews.length === 0) {
        return (
            <div style={ROW_STYLE}>
                <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                    {data.aiDescription}
                </Paragraph>
            </div>
        );
    }
    return (
        <>
            {reviews.map((review, index) => (
                <div key={index} style={index === 0 ? ROW_STYLE : ROW_BORDER_STYLE}>
                    <Rate
                        disabled
                        value={Number(review.rating) || 0}
                        style={{ fontSize: 14, color: rwColors.statusYellow }}
                    />
                    {review.title && (
                        <Text style={{ fontSize: 14, color: antGrey.grey9, fontWeight: 600 }}>{review.title}</Text>
                    )}
                    {review.content && (
                        <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                            {review.content}
                        </Paragraph>
                    )}
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        {review.author}
                        {review.submittedAt ? ` · ${dayjs(review.submittedAt).format('MMM D, YYYY')}` : ''}
                    </Text>
                </div>
            ))}
        </>
    );
};

const BetaFeedbackSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.betaFeedback;
    if (!data) {
        return null;
    }
    const feedback = data.sampleFeedback ?? [];
    if (feedback.length === 0) {
        return (
            <div style={ROW_STYLE}>
                <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                    {data.aiDescription}
                </Paragraph>
            </div>
        );
    }
    return (
        <>
            {feedback.map((item, index) => (
                <div key={index} style={index === 0 ? ROW_STYLE : ROW_BORDER_STYLE}>
                    {item.comment && (
                        <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                            {item.comment}
                        </Paragraph>
                    )}
                    {!!item.screenshotUrls?.length && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {item.screenshotUrls.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                    <img
                                        src={url}
                                        alt="Beta feedback screenshot"
                                        style={{ height: 96, borderRadius: 6, border: `1px solid ${antGrey.grey4}` }}
                                    />
                                </a>
                            ))}
                        </div>
                    )}
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        {[item.testerName, item.submittedAt ? dayjs(item.submittedAt).format('MMM D, YYYY') : '']
                            .filter(Boolean)
                            .join(' · ')}
                    </Text>
                </div>
            ))}
        </>
    );
};

const CONDITION_SYMBOL: Record<string, string> = {
    LessThan: '<',
    LessThanOrEqualTo: '≤',
    GreaterThan: '>',
    GreaterThanOrEqualTo: '≥',
};

const metricRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
        <Text style={{ color: antGrey.grey9, fontSize: 14 }}>{value}</Text>
    </div>
);

const HealthMetricsSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.healthMetrics;
    if (!data) {
        return null;
    }
    const threshold = data.configuredThreshold;
    const metricName = data.eventName || data.metricId;
    return (
        <>
            {metricName && (
                <div style={ROW_STYLE}>
                    {metricRow('Metric', data.paramName ? `${metricName} (${data.paramName})` : metricName)}
                </div>
            )}
            <div style={ROW_STYLE}>
                {metricRow('Current value', data.currentValue)}
                {threshold &&
                    metricRow('Threshold', `${CONDITION_SYMBOL[threshold.condition] ?? ''} ${threshold.value}`.trim())}
            </div>
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const RunwaySource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.runway;
    if (!data) {
        return null;
    }
    const hasTitle = !!data.title;
    return (
        <>
            {hasTitle && (
                <div style={ROW_STYLE}>
                    <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{data.title}</Text>
                </div>
            )}
            {data.description && (
                <div style={hasTitle ? ROW_BORDER_STYLE : ROW_STYLE}>
                    <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                        {data.description}
                    </Paragraph>
                </div>
            )}
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const RegressionTestingSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.regressionTesting;
    if (!data) {
        return null;
    }
    return (
        <>
            <div style={ROW_STYLE}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{data.testRunName || 'Test run'}</Text>
                    {data.testRunUrl && (
                        <a
                            href={data.testRunUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                        >
                            <ExportOutlined style={{ color: rwColors.primary, fontSize: 14 }} />
                            <Text style={{ color: rwColors.primary, fontSize: 14 }}>View run</Text>
                        </a>
                    )}
                </div>
                {data.failedTestCaseName && metricRow('Failed test case', data.failedTestCaseName)}
            </div>
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const SourceFooter: React.FC<{ createdLabel: string; updatedLabel: string }> = ({ createdLabel, updatedLabel }) => (
    <div style={ROW_BORDER_STYLE}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Created {createdLabel}</Text>
            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Updated {updatedLabel}</Text>
        </div>
    </div>
);

export const MetadataSourceSection: React.FC<{
    source: IssueSource;
    createdLabel: string;
    updatedLabel: string;
    appStoreType?: AppStoreType;
}> = ({ source, createdLabel, updatedLabel, appStoreType }) => {
    let title = '';
    let body: React.ReactNode = null;
    let icon: React.ReactNode = <IntegrationIcon integrationId={source.integrationType} size={18} />;
    if (source.type === IssueSourceType.PMTicket && source.pmTicket) {
        title = 'Ticket';
        body = <PMTicketSource source={source} />;
    } else if (source.type === IssueSourceType.UserReviews && source.userReviews) {
        title = 'User reviews';
        body = <UserReviewsSource source={source} />;
        const storeIntegrationType = appStoreType ? getIntegrationTypeForAppStoreType(appStoreType) : null;
        if (storeIntegrationType) {
            icon = <IntegrationIcon integrationId={storeIntegrationType} size={18} />;
        }
    } else if (source.type === IssueSourceType.BetaFeedback && source.betaFeedback) {
        title = 'Beta feedback';
        body = <BetaFeedbackSource source={source} />;
        // Beta feedback comes from TestFlight (the only supported provider today) and carries no integrationType.
        icon = <IntegrationIcon integrationId={IntegrationType.TestFlight} size={18} />;
    } else if (source.type === IssueSourceType.StabMon && source.healthMetrics) {
        title = 'Stability monitoring';
        body = <HealthMetricsSource source={source} />;
    } else if (source.type === IssueSourceType.OBAA && source.healthMetrics) {
        title = 'Observability & analytics';
        body = <HealthMetricsSource source={source} />;
    } else if (source.type === IssueSourceType.Runway && source.runway) {
        title = 'Runway';
        body = <RunwaySource source={source} />;
        icon = <img src={runwayLogo} width={18} height={18} alt="Runway" />;
    } else if (source.type === IssueSourceType.RegressionTesting && source.regressionTesting) {
        title = 'Regression testing';
        body = <RegressionTestingSource source={source} />;
    }
    if (!body) {
        return null;
    }
    const ticketStatus = source.type === IssueSourceType.PMTicket ? source.pmTicket?.status : undefined;
    const ticketStatusColor = tagColorForStatusCategory(source.pmTicket?.statusCategory);
    return (
        <Collapse
            className="triage-collapse"
            ghost
            bordered={false}
            items={[
                {
                    key: 'section',
                    showArrow: false,
                    style: { borderTop: `1px solid ${antGrey.grey4}` },
                    label: (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                {icon}
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{title}</Text>
                            </div>
                            {ticketStatus && (
                                <Tag
                                    color={ticketStatusColor}
                                    style={{
                                        marginRight: 0,
                                        borderRadius: 4,
                                        textTransform: 'uppercase',
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: '0.1em',
                                    }}
                                >
                                    {ticketStatus}
                                </Tag>
                            )}
                        </div>
                    ),
                    children: (
                        <>
                            {body}
                            <SourceFooter createdLabel={createdLabel} updatedLabel={updatedLabel} />
                        </>
                    ),
                },
            ]}
        />
    );
};
